import { Fragment, useState } from "react"
import classNames from "classnames"
import type { ApiTypes } from "types"

import { Pagination } from "components/common/Pagination"

import {
  customerCallsHeatmapHours,
  customerCallsSubtitle,
  customerCallsTitle,
  getCallOutcomeLabel,
  getHeatmapIntensity,
} from "./customerCalls"
import { CustomerCallsCallDetail } from "./CustomerCallsCallDetail"
import { CustomerCallsOutcomeDonut } from "./CustomerCallsOutcomeDonut"
import styles from "./CustomerCallsSection.module.scss"
import type { CustomerCallLog, CustomerCallsSection as CustomerCallsSectionData, EngagementPeriodLabels } from "./types"

const formatNumber = (value: string) => Number(value || 0).toLocaleString()
const answeredCount = (calls: CustomerCallsSectionData) =>
  Number(calls.metrics.find(item => item.id === "answered-by-human")?.value || 0)
const metricValue = (calls: CustomerCallsSectionData, id: string) =>
  calls.metrics.find(item => item.id === id)?.value ?? "0"

const getMetricTrend = (calls: CustomerCallsSectionData, id: string) => {
  const direction = calls.metrics.find(item => item.id === id)?.direction

  return {
    className: direction === "down" ? styles.trendDown : direction === "flat" ? styles.trendFlat : styles.trendUp,
    symbol: direction === "down" ? "▼" : direction === "flat" ? "—" : "▲",
  }
}
export const CustomerCallsSection = ({
  calls,
  periodLabels,
  filter = "all",
  onFilterChange = () => undefined,
  pagination,
}: {
  calls: CustomerCallsSectionData
  periodLabels?: EngagementPeriodLabels
  filter?: "all" | "answered" | "missed"
  onFilterChange?: (value: "all" | "answered" | "missed") => void
  pagination?: ApiTypes.Model.Pagination
}) => {
  const trendPeriod = periodLabels?.past ?? "past month"
  const [selectedCall, setSelectedCall] = useState<CustomerCallLog>()
  const answered = answeredCount(calls)
  const total = Number(metricValue(calls, "calls-taken") || 0)
  const answeredPercent = total ? Math.round((answered / total) * 100) : 0
  const heatmapMaximum = Math.max(...calls.callsByDayAndHour.flatMap(item => item.hours), calls.heatmapTotal ?? 0, 0)
  const rows = calls.callLogs

  return (
    <section className={styles.shell}>
      <header className={styles.header}>
        <h2 className={styles.title}>{customerCallsTitle}</h2>
        <p className={styles.subtitle}>{customerCallsSubtitle}</p>
      </header>
      <div className={styles.body}>
        <div className={styles.metricGrid}>
          {[
            ["CALLS TAKEN", "calls-taken"],
            ["CONNECTED", "connected"],
            ["ANSWERED", "answered-by-human"],
            ["AVG TALK TIME", "avg-talk-time"],
          ].map(([label, id]) => (
            <div className={styles.card} key={id}>
              <div className={styles.label}>{label}</div>
              <div className={styles.value}>{metricValue(calls, id)}</div>
              <div className={`${styles.trend} ${getMetricTrend(calls, id).className}`}>
                {getMetricTrend(calls, id).symbol}{" "}
                {Math.abs(calls.metrics.find(item => item.id === id)?.percent_change ?? 0)}% {trendPeriod}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.charts}>
          <div className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.label}>CALL OUTCOMES</div>
            <div className={styles.outcomeContent}>
              <CustomerCallsOutcomeDonut answeredPercent={answeredPercent} />
              <div className={styles.legend}>
                <span>
                  <i className={`${styles.swatch} ${styles.swatchAnswered}`} />
                  <span>Answered</span>
                  <strong>{formatNumber(String(answered))}</strong>
                </span>
                <span>
                  <i className={`${styles.swatch} ${styles.swatchMissed}`} />
                  <span>Missed</span>
                  <strong>{formatNumber(String(Math.max(total - answered, 0)))}</strong>
                </span>
              </div>
            </div>
          </div>
          <div className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.label}>CALLS BY DAY AND HOUR</div>
            <div className={styles.heatmap}>
              <span />
              {customerCallsHeatmapHours.map(hour => (
                <span key={hour}>{hour}</span>
              ))}
              {calls.callsByDayAndHour.map(item => (
                <Fragment key={item.day}>
                  <span>{item.day}</span>
                  {customerCallsHeatmapHours.map((_, column) => {
                    const value = item.hours[column] || 0

                    return (
                      <div
                        className={styles.heatmapCell}
                        key={`${item.day}-${column}`}
                        aria-label={`${item.day} ${customerCallsHeatmapHours[column]}: ${value} calls`}
                        style={{
                          backgroundColor: `rgba(106, 177, 204, ${getHeatmapIntensity(value, heatmapMaximum)})`,
                        }}
                      />
                    )
                  })}
                </Fragment>
              ))}
            </div>
            <p className={styles.heatmapDescription}>
              The same color scale is used throughout the month, so matching shades represent the same number of calls.
              Times are shown in your local time.
            </p>
            <p className={styles.heatmapSummary}>{calls.summary}</p>
          </div>
        </div>
        <div className={classNames(styles.logsHeader, styles.label)}>
          CALL LOG · {formatNumber(String(pagination?.total ?? rows.length))} CALLS THIS MONTH
        </div>
        <div className={styles.filters}>
          {(["all", "answered", "missed"] as const).map(value => (
            <button
              className={`${styles.filter} ${filter === value ? styles.filterActive : ""}`}
              type="button"
              key={value}
              onClick={() => onFilterChange(value)}
            >
              {value === "all" ? "All calls" : value === "answered" ? "Answered" : "Missed / no answer"}
            </button>
          ))}
        </div>
        <div className={styles.table}>
          <div className={styles.tableHeader} role="row">
            <span role="columnheader">Time</span>
            <span role="columnheader">Customer</span>
            <span role="columnheader">Number</span>
            <span role="columnheader">Outcome</span>
            <span role="columnheader">Duration</span>
          </div>
          {rows.map(call => (
            <button className={styles.row} type="button" key={call.id} onClick={() => setSelectedCall(call)}>
              <span>{call.timestamp}</span>
              <span>{call.customerName}</span>
              <span>{call.phone}</span>
              <span
                className={`${styles.outcome} ${call.outcome === "answered" ? styles.outcomeAnswered : styles.outcomeMissed}`}
              >
                {getCallOutcomeLabel(call.outcome)}
              </span>
              <span className={styles.duration}>{call.talkTime}</span>
            </button>
          ))}
        </div>
        <Pagination meta={pagination} singleLine />
      </div>
      {selectedCall ? <CustomerCallsCallDetail call={selectedCall} onClose={() => setSelectedCall(undefined)} /> : null}
    </section>
  )
}
